import {useContext, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {atomButtonDesigns, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import AtomImage from '@s-ui/react-atom-image'
import AtomInput from '@s-ui/react-atom-input'
import AtomValidationText, {AtomValidationTextTypes} from '@s-ui/react-atom-validation-text'
import Context from '@s-ui/react-context'
import MoleculeNotification, {
  moleculeNotificationAlignItems,
  moleculeNotificationBorderSizes,
  moleculeNotificationTypes,
  moleculeNotificationVariations
} from '@s-ui/react-molecule-notification'
import InfoIcon from '@adv-mt/mt-svg-icons/lib/InfoOutline'
import TitleBasic, {titleBasicColors, titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {BASE_CLASS, POST_CODE_REGEXP, POSTCODE_ERRORS, TRACKS} from './settings.js'

const isValidPostcode = postalCode => {
  return POST_CODE_REGEXP.test(postalCode)
}

const MOLECULE_NOTIFICATION_PROPS = {
  alignItems: moleculeNotificationAlignItems.FLEX_START,
  autoClose: false,
  effect: false,
  roundedCorners: moleculeNotificationBorderSizes.medium,
  showCloseButton: false,
  showLeftIconMobile: true,
  type: moleculeNotificationTypes.info,
  variation: moleculeNotificationVariations.positive
}

function LastCallModalContent({
  adId,
  title,
  text,
  testText,
  zipCode,
  image,
  isLongDistance,
  onConfirm,
  onClose,
  isLoading,
  showForm = true
}) {
  const {i18n, domain} = useContext(Context)
  const dataProtectionUrl = domain.get('config').get('page').DATA_PROTECTION_PATH_PATTERN
  const {track} = useAnalyticsTrack()
  const [hasErrors, setHasErrors] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [postalCode, setPostalCode] = useState(isValidPostcode(zipCode) ? zipCode : '')
  const {src, alt} = image

  const privacyPolicyLiteral = i18n.t('MODAL_LAST_CALL.PRIVACY_POLICY')

  const privacyPolicyLink = (
    <AtomButton link size={atomButtonSizes.SMALL} href={dataProtectionUrl}>
      {i18n.t('MODAL_LAST_CALL.PRIVACY_POLICY_LINK')}
    </AtomButton>
  )

  const onCTAClicked = () => {
    const isValid = isValidPostcode(postalCode)
    setHasErrors(!isValid)
    if (isValid) {
      onConfirm(postalCode)
    } else {
      track({
        event: TRACKS.POSTCODE_ERROR,
        data: {
          page_error:
            TRACKS.ERRORS[postalCode === '' ? POSTCODE_ERRORS.POSTCODE_REQUIRED : POSTCODE_ERRORS.POSTCODE_INVALID]
        }
      })
      setErrorMessage(
        i18n.t(
          `MODAL_LAST_CALL.VALIDATION_MESSAGES.${
            postalCode === '' ? POSTCODE_ERRORS.POSTCODE_REQUIRED : POSTCODE_ERRORS.POSTCODE_INVALID
          }`
        )
      )
    }
  }

  const hasZipCode = Boolean(zipCode)

  return (
    <div className={`${BASE_CLASS}-container`} data-testid="modal-lastcall-content">
      <div className={`${BASE_CLASS}-imageWrapper ${BASE_CLASS}-containerItem`}>
        <div className={`${BASE_CLASS}-image`}>
          <AtomImage src={src} alt={alt} />
        </div>
      </div>
      <div className={`${BASE_CLASS}-content ${BASE_CLASS}-containerItem`}>
        <div className={`${BASE_CLASS}-form`}>
          <div className={`${BASE_CLASS}-contentTitle`}>
            <TitleBasic
              as={titleBasicTags.h2}
              color={titleBasicColors.currentColor}
              size={titleBasicSizes.s}
              title={title}
            />
          </div>
          {text && <p className={`${BASE_CLASS}-contentText`}>{text}</p>}
          {isLongDistance && (
            <div className={`${BASE_CLASS}-longDistancePanel`}>
              <MoleculeNotification show={isLongDistance} {...MOLECULE_NOTIFICATION_PROPS}>
                <span className={`${BASE_CLASS}-longDistanceText`}>{i18n.t('MODAL_LAST_CALL.LONG_DISTANCE_INFO')}</span>
              </MoleculeNotification>
            </div>
          )}
          {showForm && (
            <>
              {!hasZipCode && (
                <div className={`${BASE_CLASS}-input`}>
                  <AtomInput
                    errorState={hasErrors}
                    onChange={e => setPostalCode(e.target.value)}
                    value={postalCode}
                    placeholder={i18n.t('MODAL_LAST_CALL.CP_PLACEHOLDER')}
                  />
                  {hasErrors && <AtomValidationText text={errorMessage} type={AtomValidationTextTypes.ERROR} />}
                </div>
              )}
              <div className={`${BASE_CLASS}-hintWrapper`}>
                <InfoIcon size="small" />
                <p className={`${BASE_CLASS}-hint`}>
                  {privacyPolicyLiteral} {privacyPolicyLink}
                </p>
              </div>
            </>
          )}
        </div>
        <div className={`${BASE_CLASS}-buttons`}>
          {showForm ? (
            <>
              <AtomButton
                className={`${BASE_CLASS}-ctaButton`}
                shape={atomButtonShapes.CIRCULAR}
                design={atomButtonDesigns.SOLID}
                size={atomButtonSizes.LARGE}
                color="accent"
                isLoading={isLoading}
                onClick={onCTAClicked}
              >
                {i18n.t('MODAL_LAST_CALL.CTA_BTN')}
              </AtomButton>
              <AtomButton
                className={`${BASE_CLASS}-ctaButton`}
                shape={atomButtonShapes.CIRCULAR}
                design={atomButtonDesigns.FLAT}
                size={atomButtonSizes.LARGE}
                onClick={onClose}
              >
                {i18n.t('MODAL_LAST_CALL.CANCEL_BTN')}
              </AtomButton>
            </>
          ) : (
            <AtomButton
              className={`${BASE_CLASS}-ctaButton`}
              shape={atomButtonShapes.CIRCULAR}
              design={atomButtonDesigns.SOLID}
              size={atomButtonSizes.LARGE}
              color="primary"
              onClick={onClose}
            >
              {i18n.t('MODAL_LAST_CALL.CONFIRMATION_BTN')}
            </AtomButton>
          )}
        </div>
      </div>
    </div>
  )
}

LastCallModalContent.displayName = 'LastCallModalContent'
LastCallModalContent.propTypes = {
  adId: PropTypes.string,
  image: PropTypes.shape({
    alt: PropTypes.string,
    height: PropTypes.string,
    src: PropTypes.string.isRequired,
    width: PropTypes.string
  }).isRequired,
  isLoading: PropTypes.bool,
  isLongDistance: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  showForm: PropTypes.bool,
  text: PropTypes.string.isRequired,
  testText: PropTypes.string,
  title: PropTypes.string.isRequired,
  zipCode: PropTypes.string
}

export {LastCallModalContent}
